import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from './axiosInstance';
import { useFitToViewport } from './ShortTermForecast';

/**
 * OverviewTab — the landing page of Sales Forecast & Rollout.
 *
 * One combined grid for EVERY customer: 16 weekly columns (from the
 * Short-Term tab) followed by the remaining months of the rolling 12-month
 * horizon (from the Long-Term tab) — the same layout the Excel export's
 * "Full Forecast" sheet uses, so what you see is what the boss gets.
 */
function OverviewTab({ onOpenSnapshot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  useFitToViewport(scrollRef);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/forecast/overview');
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching overview:', err);
      setError('Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleExport = async () => {
    try {
      const res = await axiosInstance.get('/forecast/store-types/export-excel', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.document' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `forecast-${new Date().getFullYear()}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(err.response?.data?.error || 'Export failed');
    }
  };

  const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // W## + "Mon D" — same convention as the Short-Term tab headers
  const weekLabel = (weekStart) => {
    const d = new Date(weekStart + 'T00:00:00');
    const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = target.getUTCDay() || 7;
    target.setUTCDate(target.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
    return { line1: `W${weekNum}`, line2: `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}` };
  };

  const monthLabel = (monthStr) => {
    const [y, m] = monthStr.split('-');
    return { line1: SHORT_MONTHS[parseInt(m, 10) - 1], line2: `'${y.slice(2)}` };
  };

  const fmt = (n) => (n ? n.toLocaleString() : '-');

  // Current calendar week's Monday (local) — highlights that column.
  const currentWeekKey = (() => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const rowLabel = (r) =>
    `${r.name}${r.bezel ? ` (${r.bezel === 'white' ? 'White' : r.bezel === 'red' ? 'Red' : 'Black'})` : ''}`;

  if (error) return <div className="srf-error">{error}</div>;

  return (
    <div className="srf-store-types-section">
      <div className="srf-section-header">
        <h2>Full Forecast — All Customers</h2>
        <div className="srf-section-header-right">
          <button className="srf-btn srf-btn-secondary" onClick={handleExport}>
            Export to Excel
          </button>
        </div>
      </div>

      <p className="srf-covered-legend">
        16-week columns come from the Short-Term tab · month columns are the rest of the
        rolling 12-month Long-Term forecast. Edit on those tabs — this page is the rollup.
      </p>

      {loading ? (
        <div className="srf-loading">Loading...</div>
      ) : !data || data.customers.length === 0 ? (
        <div className="srf-empty">
          No forecast data yet. Add models on the Short-Term or Long-Term tabs.
        </div>
      ) : (
        <div className="srf-store-types-list">
          <div className="srf-store-type-card srf-ov-card">
            <div className="srf-monthly-grid srf-ov-readonly">
              <div className="srf-st-scroll" ref={scrollRef}>
                <div className="srf-ov-table">
                  {/* Header row */}
                  <div className="srf-st-row srf-st-header-row">
                    <div className="srf-st-product-cell srf-st-header-cell">Customer / Model</div>
                    {data.weeks.map(w => {
                      const lbl = weekLabel(w);
                      const isCur = w === currentWeekKey;
                      const trigger = !!onOpenSnapshot;
                      return (
                        <div
                          key={w}
                          className={`srf-st-week-cell srf-st-header-cell ${isCur ? 'srf-week-current' : ''} ${trigger ? 'srf-week-trigger' : ''}`}
                          onClick={trigger ? () => onOpenSnapshot(w) : undefined}
                          role={trigger ? 'button' : undefined}
                          tabIndex={trigger ? 0 : undefined}
                          title={trigger ? `Open snapshot of ${lbl.line1}` : undefined}
                          onKeyDown={trigger ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSnapshot(w); } } : undefined}
                        >
                          <div className="srf-st-week-label-1">{lbl.line1}</div>
                          <div className="srf-st-week-label-2">{lbl.line2}</div>
                        </div>
                      );
                    })}
                    {data.months.map((m, i) => {
                      const lbl = monthLabel(m);
                      return (
                        <div key={m} className={`srf-st-week-cell srf-st-header-cell srf-ov-month-cell ${i === 0 ? 'srf-ov-month-first' : ''}`}>
                          <div className="srf-st-week-label-1 srf-ov-month-label">{lbl.line1}</div>
                          <div className="srf-st-week-label-2">{lbl.line2}</div>
                        </div>
                      );
                    })}
                    <div className="srf-st-total-cell srf-st-header-cell">Total</div>
                  </div>

                  {/* One section per customer */}
                  {data.customers.map(c => (
                    <React.Fragment key={c.company}>
                      <div className="srf-ov-company-row">
                        <span className="srf-ov-company-name">{c.company}</span>
                        <span className="srf-ov-company-total">
                          {c.grandTotal.toLocaleString()} total labels
                        </span>
                      </div>
                      {c.rows.map(r => (
                        <div key={`${r.name}|${r.bezel}|${r.modelCode}`} className="srf-st-row">
                          <div className="srf-st-product-cell">
                            <div className="srf-st-product-name" title={`${rowLabel(r)}${r.modelCode ? ` - ${r.modelCode}` : ''}`}>
                              {rowLabel(r)}
                            </div>
                            {r.modelCode && (
                              <div className="srf-st-product-code">{r.modelCode}</div>
                            )}
                          </div>
                          {r.weekly.map((v, i) => (
                            <div key={data.weeks[i]} className={`srf-st-week-cell ${data.weeks[i] === currentWeekKey ? 'srf-week-current' : ''}`}>
                              <div className={`srf-month-value ${v ? 'has-value' : ''}`}>{fmt(v)}</div>
                            </div>
                          ))}
                          {r.monthly.map((v, i) => (
                            <div key={data.months[i]} className={`srf-st-week-cell srf-ov-month-cell ${i === 0 ? 'srf-ov-month-first' : ''}`}>
                              <div className={`srf-month-value ${v ? 'has-value' : ''}`}>{fmt(v)}</div>
                            </div>
                          ))}
                          <div className="srf-st-total-cell">
                            <div className="srf-month-value has-value">{fmt(r.total)}</div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OverviewTab;
